import { Form, Input, Button, Card, Typography, message, Alert, Divider } from 'antd';
import { UserOutlined, LockOutlined, BankOutlined, GoogleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '../../stores/authStore';

const { Title } = Typography;

// Google Client ID - should be in env variable
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    login,
    googleLogin,
    isLoading,
    error,
    isAuthenticated,
    clearError
  } = useAuthStore();

  // 如果已經登錄，重定向到AI CRM概覽頁
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (isAuthenticated && token) {
      navigate('/ai-crm/overview');
    }
  }, [isAuthenticated, navigate]);

  // 若是因「在其他裝置登入」被踢出，顯示友善提示
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('loginRedirectReason');
      if (reason === 'SESSION_INVALIDATED') {
        message.warning('You were signed out because your account was used to sign in on another device.');
        sessionStorage.removeItem('loginRedirectReason');
      }
    } catch {/* ignore */}
  }, []);

  // 清除錯誤信息
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Handle Google login callback
  const handleGoogleCallback = useCallback(async (response: any) => {
    try {
      setGoogleLoading(true);

      // Decode JWT to get user info
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const googleId = payload.sub;
      const email = payload.email;

      await googleLogin({ googleId, email });
      message.success('Google 登錄成功！');
      const next = new URLSearchParams(window.location.search).get('next');
      navigate(next || '/ai-crm/overview');
    } catch (error: any) {
      message.error(error.message || 'Google 登錄失敗');
    } finally {
      setGoogleLoading(false);
    }
  }, [googleLogin, navigate]);

  // Initialize Google Sign-In
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('Google Client ID not configured');
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        });

        const buttonDiv = document.getElementById('google-signin-button');
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: 'signin_with',
          });
        }
      }
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [handleGoogleCallback]);

  const onFinish = async (values: any) => {
    try {
      await login({
        email: values.email,
        password: values.password,
        companyId: values.companyId?.trim() || undefined
      });
      message.success('登錄成功！');
      const next = new URLSearchParams(window.location.search).get('next');
      navigate(next || '/ai-crm/overview');
    } catch (error: any) {
      message.error(error.message || '登錄失敗');
    }
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card style={{
        width: '100%',
        maxWidth: 400,
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2} style={{ marginBottom: '8px', color: '#005b96' }}>
            WYNI AI ERP
          </Title>
          <Typography.Text type="secondary">
            歡迎回來
          </Typography.Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={clearError}
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Google Sign-In Button */}
        {GOOGLE_CLIENT_ID && (
          <>
            <div
              id="google-signin-button"
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '8px'
              }}
            />
            {googleLoading && (
              <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                <Typography.Text type="secondary">正在驗證 Google 帳戶...</Typography.Text>
              </div>
            )}
            <Divider style={{ margin: '16px 0' }}>
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                或使用公司帳戶登錄
              </Typography.Text>
            </Divider>
          </>
        )}

        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="companyId"
            rules={[
              { required: true, message: '請輸入公司 ID' }
            ]}
          >
            <Input
              prefix={<BankOutlined />}
              placeholder="公司 ID (Company ID)"
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: '請輸入郵箱' },
              { type: 'email', message: '請輸入有效的郵箱地址' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="郵箱地址"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 6, message: '密碼至少需要6個字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密碼"
            />
          </Form.Item>


          <Form.Item style={{ marginBottom: '16px' }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={isLoading}
              style={{
                height: '48px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              登錄
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Don't have an account?{' '}
              <Link to="/register" style={{ color: '#005b96' }}>
                Sign up
              </Link>
            </Typography.Text>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;
